import type {Dispatch} from 'react'

import type {ItemUpdatesWithSourceRepoId, UpdateColumnValueAction} from '../../../../api/columns/contracts/storage'
import {getColumnModel} from '../../../../hooks/tanstack-table/helpers/column-api'
import type {Cell} from '../../../../hooks/tanstack-table/helpers/types'
import type {MemexItemModel} from '../../../../models/memex-item-model'
import {HistoryResources} from '../../../../strings'
import type {AddToastProps} from '../../../toasts/use-toasts'
import type {SelectionMethods} from '../../table-cell-bulk-selection'
import type {TableDataType} from '../../table-data-type'
import {buildPlaintextTableForClipboardContent} from './build-plaintext-table-for-clipboard-content'
import {getBehaviorForColumn} from './columns'
import {ClipboardActionTypes} from './constants'
import {PasteValidationFailureError} from './errors'
import {getOnlyCell} from './get-only-cell'
import type {ClipboardAction, ClipboardContent, ClipboardState} from './types'
import {isClipboardColumnModel} from './types'

/**
 * Determines the target items for pasting based on selection and clipboard content
 */
function getTargetItems({
  shouldUseMultiValuePaste,
  selectedItems,
  clipboardRows,
  cell,
  getVisibleRows,
}: {
  shouldUseMultiValuePaste: boolean | undefined
  selectedItems: Array<MemexItemModel>
  clipboardRows: Array<Array<ClipboardContent | undefined>>
  cell: Pick<Cell<TableDataType>, 'column' | 'row'>
  getVisibleRows?: () => Array<{id: string | number; original: MemexItemModel}>
}): Array<MemexItemModel> {
  if (!shouldUseMultiValuePaste) {
    return selectedItems.length === 0 ? [cell.row.original] : selectedItems
  }

  if (selectedItems.length <= 1 && getVisibleRows) {
    const visibleRows = getVisibleRows()
    const currentRowIndex = visibleRows.findIndex(row => String(row.original.id) === String(cell.row.original.id))

    if (currentRowIndex === -1) {
      return [cell.row.original]
    }

    return visibleRows.slice(currentRowIndex, currentRowIndex + clipboardRows.length).map(row => row.original)
  }

  if (selectedItems.length > 1) {
    return selectedItems.slice(0, clipboardRows.length)
  }

  return [cell.row.original]
}

/**
 * Extracts paste content from a clipboard row, handling empty cells
 *
 * When users copy data from a table, some cells may be empty (undefined) while others contain data.
 * For example, copying a row might result in: [undefined, "Done", undefined, "Bug fix"]
 *
 * We need to find the first cell that actually contains data rather than just using clipboardRow[0]
 * which might be undefined if the first column in the copied selection was empty.
 */
function getPasteContent(clipboardRow: Array<ClipboardContent | undefined> | undefined): ClipboardContent | string {
  if (!clipboardRow) return ''

  const firstCell = clipboardRow.find(cellContent => cellContent !== undefined)
  if (!firstCell) {
    return ''
  }

  const hasStructuredData = firstCell.dataType && firstCell.columnId
  const pasteContent: ClipboardContent | string = hasStructuredData ? firstCell : firstCell.text

  if (pasteContent === null || pasteContent === undefined) {
    return ''
  }

  return pasteContent
}

function getSourceRepoFromPasteContent(pasteContent: ClipboardContent | string): number | null {
  if (typeof pasteContent === 'object' && 'repositoryId' in pasteContent) {
    return pasteContent.repositoryId
  }
  return null
}

type Props = {
  cell: Pick<Cell<TableDataType>, 'column' | 'row'>
  selectedItems: Array<MemexItemModel>
  currentClipboard: ClipboardState
  clipboardDispatch: Dispatch<ClipboardAction>
  updateItems: (
    items: Array<MemexItemModel>,
    update: UpdateColumnValueAction,
    description?: string,
    sourceRepoId?: number | null,
  ) => Promise<void>
  updateItemsMultipleValues: (itemUpdates: Array<ItemUpdatesWithSourceRepoId>, description?: string) => Promise<void>
  addToast: (args: AddToastProps) => number
  multiValueEditEnabled?: boolean
  getVisibleRows?: () => Array<{id: string | number; original: MemexItemModel}>
  replaceSelectedRows?: SelectionMethods['replaceRange']
}

export const updateItemsOrError = async ({
  cell,
  selectedItems,
  currentClipboard,
  clipboardDispatch,
  updateItems,
  updateItemsMultipleValues,
  addToast,
  multiValueEditEnabled,
  getVisibleRows,
  replaceSelectedRows,
}: Props) => {
  let navigatorClipboardText = ''
  try {
    navigatorClipboardText = await navigator.clipboard.readText()
  } catch {
    // can be permission denied - in which case we just ignore
  }

  let currentClipboardState: ClipboardState = currentClipboard
  let shouldUseNavigatorClipboard = false

  // If the current clipboard is empty and
  // navigator.clipboard is not, then use navigator.clipboard
  if (currentClipboard.type === 'empty') {
    shouldUseNavigatorClipboard = !!navigatorClipboardText
  }

  // If the current clipboard is populated (not empty) and
  // navigator.clipboard is not empty and different than the current clipboard, use navigator.clipboard
  if (currentClipboard.type === 'populated') {
    shouldUseNavigatorClipboard =
      !!navigatorClipboardText &&
      navigatorClipboardText !== buildPlaintextTableForClipboardContent(currentClipboard.value)
  }

  if (shouldUseNavigatorClipboard) {
    const lines = navigatorClipboardText.split('\n')

    currentClipboardState = {
      type: 'populated',
      value: lines.map(line => [
        {
          dataType: 'text',
          text: line.trim(),
          value: {
            raw: line.trim(),
            html: line.trim(),
          },
        },
      ]),
      hideCopyOutline: false,
      copiedCells: new Map(),
    }

    // Clear the current clipboard, as it is no longer in sync with navigator.clipboard
    clipboardDispatch({
      type: ClipboardActionTypes.CLEAR_CLIPBOARD,
    })
  }

  // This shouldn't happen given the above implementation
  if (currentClipboardState.type === 'empty') {
    return
  }

  const columnModel = getColumnModel(cell.column)
  if (!columnModel) return

  // Check if we can paste to this column
  if (!isClipboardColumnModel(columnModel)) {
    return
  }

  const behavior = getBehaviorForColumn(columnModel)
  // Some column types do not support pasting to set value. For these cases, simply ignore the paste event.
  if (!('buildUpdate' in behavior)) return

  const clipboardRows = currentClipboardState.value

  const shouldUseMultiValuePaste = multiValueEditEnabled && clipboardRows.length > 1

  const targetItems = getTargetItems({
    shouldUseMultiValuePaste: Boolean(shouldUseMultiValuePaste),
    selectedItems,
    clipboardRows,
    cell,
    getVisibleRows,
  })

  if (shouldUseMultiValuePaste && targetItems.length > 0) {
    const itemUpdates: Array<ItemUpdatesWithSourceRepoId> = []

    // Update selection range to match pasted content
    replaceSelectedRows?.(
      cell.column.id,
      targetItems.map(item => item.id),
    )

    for (const [index, clipboardRow] of clipboardRows.slice(0, targetItems.length).entries()) {
      const targetItem = targetItems[index]
      if (!clipboardRow || !targetItem) {
        continue
      }

      const pasteContent = getPasteContent(clipboardRow)

      try {
        const update = behavior.buildUpdate(pasteContent, columnModel)
        if (update && targetItem.id) {
          const sourceRepo = getSourceRepoFromPasteContent(pasteContent)
          itemUpdates.push({itemId: targetItem.id, updates: [update], sourceRepoId: sourceRepo})
        }
      } catch (error) {
        if (error instanceof PasteValidationFailureError) {
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({
            message: error.message,
            type: 'warning',
          })
        } else {
          throw error
        }
      }
    }

    if (itemUpdates.length > 0) {
      // Remove the `is-copy-source` state from pasted cells. NOTE: This behavior is only desired for multi-row pastes.
      clipboardDispatch({
        type: ClipboardActionTypes.PASTE_CLIPBOARD_MULTIROW,
      })

      await updateItemsMultipleValues(itemUpdates, HistoryResources.paste)
    }
  } else {
    const firstCellContent = getOnlyCell(currentClipboardState.value)
    if (!firstCellContent) return

    // If there is no column associated with the current clipboard contents, just paste the text from the
    // clipboard instead, without any context of where it came from. This is usually the case when the user
    // has copied text from outside of memex and is trying to paste it into the project.
    const pasteContent =
      firstCellContent.dataType && firstCellContent.columnId ? firstCellContent : firstCellContent.text

    if (!pasteContent) return

    const sourceRepo = getSourceRepoFromPasteContent(pasteContent)

    try {
      const update = behavior.buildUpdate(pasteContent, columnModel)
      if (update) {
        await updateItems(targetItems, update, HistoryResources.paste, sourceRepo)
      }
    } catch (error) {
      if (error instanceof PasteValidationFailureError) {
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({
          message: error.message,
          type: 'warning',
        })
      } else {
        throw error
      }
    }
  }
}
